import { inject, Injectable, signal } from '@angular/core';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { distinctUntilChanged, interval, switchMap, Subscription } from 'rxjs';
import { CampaignInstanceMessageDto } from '@shared/api/campaign-instance-messages.interfaces';
import { CampaignInstancesApi } from '@shared/api/campaign-instances.api';

export enum MessageNotificationKind {
  Responsible = 0,
  CallCenter = 1,
  Observer = 2,
}

export interface MessagePollingItemDto {
  campaignInstanceGuid: string;
  unreadCount: number;
  kind: MessageNotificationKind;
}

export interface GlobalMessagePollingResponse {
  items: MessagePollingItemDto[];
}

export interface MessageNotificationDto {
  campaignInstanceGuid: string;
  campaignGuid?: string;
  campaignName?: string;
  participantName?: string;
  participantNif?: string;
  unreadCount: number;
  kind: MessageNotificationKind;
}

export interface UpdateMessageStateDto {
  campaignInstanceGuid: string;
}

@Injectable({
  providedIn: 'root',
})
export class MessagePollingService {
  private readonly api = inject(ApiService);
  private readonly campaignInstancesApi = inject(CampaignInstancesApi);
  private readonly authService = inject(AuthService);
  private pollingSub?: Subscription;
  private authSub?: Subscription;
  private messagePollingSub?: Subscription;
  private notificationKindCache: Record<string, MessageNotificationKind> = {};

  // Global state signals
  public notifications = signal<MessageNotificationDto[]>([]);
  public totalUnreadCount = signal<number>(0);
  private campaignInstanceCache: Record<string, CachedInstanceInfo> = {};

  // Latest messages for the tracked conversation
  public currentChatMessages = signal<CampaignInstanceMessageDto[]>([]);

  // Current context (if user is inside a chat)
  private currentChatGuid: string | null = null;
  private trackedConversationGuid: string | null = null;

  private isExternalUser(): boolean {
    return localStorage.getItem('auth_type') === 'jwt';
  }

  private resetUiState() {
    this.notifications.set([]);
    this.totalUnreadCount.set(0);
    this.currentChatMessages.set([]);
    this.notificationKindCache = {};
  }

  private startGlobalInterval() {
    if (this.pollingSub) return;
    this.pollingSub = interval(10000)
      .pipe(switchMap(() => this.performPoll()))
      .subscribe();
  }

  private stopGlobalInterval() {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  startPolling() {
    if (this.authSub) return;

    // Run an instant poll as soon as we become logged in (fixes "wait for interval after reload/login")
    this.authSub = this.authService.isLoggedInStream.pipe(distinctUntilChanged()).subscribe(isLoggedIn => {
      // External (JWT) users do not participate in communications/notifications.
      if (!isLoggedIn || this.isExternalUser()) {
        this.stopGlobalInterval();
        this.stopMessagePolling();
        this.resetUiState();
        return;
      }

      this.startGlobalInterval();
      void this.performPoll();
      if (this.trackedConversationGuid) {
        this.startMessagePolling(this.trackedConversationGuid);
      }
    });
  }

  stopPolling() {
    this.stopGlobalInterval();
    this.authSub?.unsubscribe();
    this.authSub = undefined;
    this.stopMessagePolling();
  }

  setCurrentChat(guid: string | null) {
    this.currentChatGuid = guid;
  }

  trackMessagesFor(guid: string | null) {
    if (this.isExternalUser()) {
      this.trackedConversationGuid = null;
      this.stopMessagePolling();
      this.currentChatMessages.set([]);
      return;
    }
    if (this.trackedConversationGuid === guid) return;
    this.trackedConversationGuid = guid;
    if (guid) {
      this.startMessagePolling(guid);
    } else {
      this.stopMessagePolling();
      this.currentChatMessages.set([]);
    }
  }

  async refreshTrackedMessages(force = false): Promise<void> {
    if (this.trackedConversationGuid) {
      await this.loadMessages(this.trackedConversationGuid, force);
    }
  }

  private async performPoll(): Promise<GlobalMessagePollingResponse> {
    // If not logged in, avoid calling the backend (prevents 403 during startup/session restore)
    // External (JWT) users do not participate in communications/notifications.
    if (!this.authService.isLoggedIn || this.isExternalUser()) {
      this.resetUiState();
      return { items: [] };
    }

    const params = this.currentChatGuid ? `?currentCampaignInstanceGuid=${this.currentChatGuid}` : '';
    const response = await this.api.get<GlobalMessagePollingResponse>(`messages/polling${params}`);
    this.notificationKindCache = {};
    (response.items ?? []).forEach(item => {
      this.notificationKindCache[item.campaignInstanceGuid] = item.kind;
    });

    const notifications = await this.buildNotifications(response.items ?? []);
    this.notifications.set(notifications);
    this.totalUnreadCount.set(notifications.reduce((sum, notification) => sum + notification.unreadCount, 0));
    return response;
  }

  private kindSortOrder(kind: MessageNotificationKind): number {
    switch (kind) {
      case MessageNotificationKind.Responsible:
        return 0;
      case MessageNotificationKind.CallCenter:
        return 1;
      case MessageNotificationKind.Observer:
        return 2;
      default:
        return 99;
    }
  }

  private async buildNotifications(items: MessagePollingItemDto[]): Promise<MessageNotificationDto[]> {
    if (!items.length) return [];

    const filtered = items.filter(i => i.unreadCount > 0);
    if (!filtered.length) return [];

    await this.ensureCampaignInfo(filtered.map(i => i.campaignInstanceGuid));

    return filtered
      .map(item => {
        const guid = item.campaignInstanceGuid;
        const count = item.unreadCount;
        const cached = this.campaignInstanceCache[guid];
        return {
          campaignInstanceGuid: guid,
          campaignGuid: cached?.campaignGuid,
          campaignName: cached?.campaignName ?? guid,
          participantName: cached?.participantName,
          participantNif: cached?.participantNif,
          unreadCount: count,
          kind: item.kind,
        };
      })
      .sort((a, b) => {
        const byKind = this.kindSortOrder(a.kind) - this.kindSortOrder(b.kind);
        if (byKind !== 0) return byKind;
        return b.unreadCount - a.unreadCount;
      });
  }

  private async ensureCampaignInfo(guids: string[]) {
    const missing = guids.filter(guid => !this.campaignInstanceCache[guid]);
    if (!missing.length) return;

    const fetched = await this.campaignInstancesApi.getMultiple(missing);
    fetched.forEach(instance => {
      this.campaignInstanceCache[instance.campaignInstanceGuid] = {
        campaignGuid: instance.campaignGuid,
        campaignName: instance.campaignName ?? instance.name ?? '',
        participantName: instance.name ?? '',
        participantNif: instance.nif ?? '',
      };
    });
  }

  private updateNotificationCounter(guid: string, count: number) {
    const current = [...this.notifications()];
    const index = current.findIndex(n => n.campaignInstanceGuid === guid);
    if (index >= 0) {
      current[index] = { ...current[index], unreadCount: count };
    } else if (count > 0) {
      const cached = this.campaignInstanceCache[guid];
      current.push({
        campaignInstanceGuid: guid,
        campaignGuid: cached?.campaignGuid,
        campaignName: cached?.campaignName ?? guid,
        participantName: cached?.participantName,
        participantNif: cached?.participantNif,
        unreadCount: count,
        kind: this.notificationKindCache[guid] ?? MessageNotificationKind.Responsible,
      });
    }
    const filtered = current.filter(n => n.unreadCount > 0);
    this.notifications.set(filtered);
    this.totalUnreadCount.set(filtered.reduce((sum, n) => sum + n.unreadCount, 0));
  }

  private startMessagePolling(guid: string) {
    this.stopMessagePolling();
    void this.loadMessages(guid, true);
    this.messagePollingSub = interval(10000).subscribe(() => {
      void this.loadMessages(guid);
    });
  }

  private stopMessagePolling() {
    this.messagePollingSub?.unsubscribe();
    this.messagePollingSub = undefined;
  }

  private async loadMessages(guid: string, force = false) {
    if (!this.authService.isLoggedIn || this.isExternalUser()) {
      return;
    }
    if (!force && this.notifications().find(n => n.campaignInstanceGuid === guid)?.unreadCount === 0) {
      return;
    }

    try {
      const messages = await this.api.get<CampaignInstanceMessageDto[]>(`campaign-instances/messages?campaignInstanceGuid=${guid}`);
      this.currentChatMessages.set(messages);
    } catch (error) {
      console.warn('Failed to load messages for polling', error);
    }
  }

  async markAsRead(guid: string): Promise<void> {
    if (!this.authService.isLoggedIn || this.isExternalUser()) {
      return;
    }
    // Call backend to mark as read specifically (if not handled by polling context)
    const body: UpdateMessageStateDto = { campaignInstanceGuid: guid };
    await this.api.post<void>('messages/read', body);
    this.updateNotificationCounter(guid, 0);
  }
}

interface CachedInstanceInfo {
  campaignGuid?: string;
  campaignName?: string;
  participantName?: string;
  participantNif?: string;
}
